import { CloseRounded } from '@mui/icons-material'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import SettingsIcon from '@mui/icons-material/Settings'
import { LoadingButton, Timeline } from '@mui/lab'
import TimelineConnector from '@mui/lab/TimelineConnector'
import TimelineContent from '@mui/lab/TimelineContent'
import TimelineDot from '@mui/lab/TimelineDot'
import TimelineItem, { timelineItemClasses } from '@mui/lab/TimelineItem'
import TimelineSeparator from '@mui/lab/TimelineSeparator'
import { Box, Button, Card, CardContent, Chip, IconButton, Modal, Skeleton, Stack, Tooltip, Typography } from '@mui/material'
import { memo, useState } from 'react'
import { useDispatch } from 'react-redux'
import ApiClient from '../../backend/ApiClient'
import FlowApiClient from '../../backend/sub_api_clients/FlowApiClient'
import algo from '../../core/Algo'
import { statusOptions } from '../../definitions/Approval'
import { IFlow } from '../../definitions/Nodes'
import Formatter from '../../helpers/Formatter'
import { changeRoute, overwriteRouteParams } from '../../store/slices/SessionSlice'
import useCommand from '../hooks/useCommand'
import useInvalidate from '../hooks/useInvalidate'
import useQuery, { buildQueryKey } from '../hooks/useQuery'
import { snackbar } from '../services/SnackbarService'

interface P {
    flow: IFlow
}

const FlowSettings = ({ flow }: P) => {
    const dispatch = useDispatch()
    const [openSettingsModal, setOpenSettingsModal] = useState(false)
    const [pendingCloneId, setPendingCloneId] = useState<string>(null)
    const invalidateApproval = useInvalidate({ queryKey: buildQueryKey(`get-approval-${flow.uuid}`) })
    const invalidateFlow = useInvalidate({ queryKey: buildQueryKey(`get-flow-${flow.uuid}`) })
    const invalidateVersions = useInvalidate({ queryKey: buildQueryKey(`get-all-version-flows-/*`) })
    const invalidateApprovedTemplates = useInvalidate({ queryKey: buildQueryKey('approvedEngines') })
    const invalidatePendingTemplates = useInvalidate({ queryKey: buildQueryKey('pendingEngines') })

    const { status, response } = useQuery({
        queryKey: buildQueryKey('get-all-version-flows-' + flow.versioning.ancestorKey),
        queryFn: () => FlowApiClient.getAllVersionFlows(flow.versioning.ancestorKey),
        cache: true
    })

    const { execute, status: cloningStatus } = useCommand({
        commandFn: (flowId: string) => ApiClient.Flow.createNewFlowVersion(flowId),
        onFinish: result => {
            setPendingCloneId(null)
            if (result.status === 'success') {
                invalidateVersions()
                invalidateApproval()
                invalidateFlow()
                invalidatePendingTemplates()
                invalidateApprovedTemplates()
                dispatch(changeRoute({
                    route: 'flow',
                    params: { flowId: result?.response?.uuid }
                }))
            }
            if (result.status === 'error') {
                snackbar.show({ severity: 'error', text: 'Failed to create new flow version' })
            }
        }
    })

    const startClone = (flowId: string) => {
        // If a clone is already in progress, ignore additional clicks
        if (cloningStatus === 'loading') return
        setPendingCloneId(flowId)
        execute(flowId)
    }

    const orderdVersionSimpleFlow = response?.length > 0 ? algo.orderBy(response, x => x.versioning.version, 'desc') : []

    const openFlowVersion = (flowId: string) => {
        invalidateVersions()
        invalidateApproval()
        invalidateFlow()
        invalidatePendingTemplates()
        invalidateApprovedTemplates()
        dispatch(overwriteRouteParams({ flowId: flowId }))
        setOpenSettingsModal(false)
    }

    const isCloning = cloningStatus === 'loading'

    const renderLoading = () => {
        return (
            <div>
                {Array(5).fill(0).map((_, i) => {
                    return (
                        <TimelineItem key={i}>
                            <TimelineSeparator>
                                <TimelineConnector />
                                <TimelineDot color='secondary'>
                                    <AccountTreeIcon />
                                </TimelineDot>
                                <TimelineConnector />
                            </TimelineSeparator>
                            <TimelineContent>
                                <Typography variant='h6' component='span'>
                                    <Skeleton variant='text' sx={{ fontSize: 'h6.fontSize', width: 200 }} />
                                </Typography>
                                <Typography variant='body2' marginLeft={1} color='text.secondary'>
                                    <Skeleton variant='text' sx={{ fontSize: 'body2.fontSize', width: 100 }} />
                                </Typography>
                                <Box marginTop={1} gap={2} display='flex' flexDirection='row'>
                                    <Button disabled={isCloning}>
                                        <Skeleton variant='text' sx={{ fontSize: 'body2.fontSize', width: 50 }} />
                                    </Button>
                                    <Button disabled={isCloning}>
                                        <Box sx={{ alignItems: 'center', justifyContent: 'center', display: 'flex', flexDirection: 'row' }}>
                                            <Skeleton variant='text' sx={{ fontSize: 'body2.fontSize', width: 50 }} />
                                        </Box>
                                    </Button>
                                </Box>
                            </TimelineContent>
                        </TimelineItem>
                    )
                })}
            </div>
        )
    }

    return (
        <>
            <Box sx={{ display: 'flex', flexDirection: 'column', position: 'relative' }}>
                <Tooltip title='Flow setting' arrow>
                    <SettingsIcon onClick={() => setOpenSettingsModal(true)} />
                </Tooltip>
            </Box>
            <Modal open={openSettingsModal} onClose={() => setOpenSettingsModal(false)} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                <Card sx={{ padding: 2, width: '50vw' }}>
                    <Stack flexDirection='row' justifyContent='space-between' alignItems='center'>
                        <Typography variant='h6'>Flow Settings</Typography>
                        <IconButton size='small' onClick={() => setOpenSettingsModal(false)}>
                            <CloseRounded fontSize='small' />
                        </IconButton>
                    </Stack>
                    <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflowY: 'auto', maxHeight: '75vh', padding: 0, marginTop: 2 }}>
                        <Box>
                            <Typography variant='subtitle2' marginBottom={2}>Flow Information</Typography>
                            <Box display='grid' gridTemplateColumns='repeat(2, 1fr)' gap={1}>
                                <Box>
                                    <Typography variant='body2' color={theme => theme.palette.text.secondary}>Name</Typography>
                                    <Typography variant='body2'>{flow.name}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant='body2' color={theme => theme.palette.text.secondary}>Version</Typography>
                                    <Typography variant='body2'>{flow.versioning.version}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant='body2' color={theme => theme.palette.text.secondary}>Created at</Typography>
                                    <Typography variant='body2'>{Formatter.date(new Date(flow.createdAt))}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant='body2' color={theme => theme.palette.text.secondary}>Last Modify</Typography>
                                    <Typography variant='body2'>{Formatter.date(new Date(flow.lastModified))}</Typography>
                                </Box>
                            </Box>
                        </Box>
                        <Box marginTop={2} sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
                            <Typography variant='subtitle2'>
                                Versioning
                            </Typography>
                            <Box sx={{ overflow: 'auto', flex: 1, display: 'flex', flexDirection: 'column', paddingTop: 2, paddingBottom: 2, paddingRight: 2 }}>
                                <Timeline sx={{ [`& .${timelineItemClasses.root}:before`]: { flex: 0, padding: 0 }, [`& .MuiTimelineConnector-root`]: { height: 30 } }}>
                                    {status === 'loading'
                                        ? renderLoading()
                                        : orderdVersionSimpleFlow.map(x => {
                                            const isThisFlow = x.uuid === flow.uuid
                                            const isThisPending = pendingCloneId === x.uuid

                                            return (
                                                <TimelineItem key={x.uuid}>
                                                    <TimelineSeparator>
                                                        <TimelineConnector />
                                                        <TimelineDot color={isThisFlow ? 'primary' : 'inherit'}>
                                                            <AccountTreeIcon />
                                                        </TimelineDot>
                                                        <TimelineConnector />
                                                    </TimelineSeparator>
                                                    <TimelineContent>
                                                        <Typography variant='h6' component='span' fontWeight={x?.versioning?.isHead ? 'bold' : 'regular'}>
                                                            Version {x.versioning.version}
                                                        </Typography>
                                                        {x.approval && (
                                                            <Chip
                                                                sx={{
                                                                    borderRadius: '999px',
                                                                    fontWeight: 600,
                                                                    ml: 2
                                                                }}
                                                                label={(
                                                                    <Box display='flex' alignItems='center' gap={1}>
                                                                        <Box
                                                                            width={8}
                                                                            height={8}
                                                                            borderRadius='50%'
                                                                            bgcolor={
                                                                                (x.approval.status === 'approved' && x.versioning.isHead)
                                                                                    ? 'success.main'
                                                                                    : (x.approval.status === 'approved' && !x.versioning.isHead)
                                                                                            ? 'grey.500'
                                                                                            : x.approval.status === 'ready-for-review'
                                                                                                ? 'info.main'
                                                                                                : x.approval.status === 'rejected'
                                                                                                    ? 'red'
                                                                                                    : 'orange'
                                                                            }
                                                                        />
                                                                        <Typography variant='body2'>
                                                                            {statusOptions.find(option => option.value === x.approval?.status)?.label}
                                                                        </Typography>
                                                                    </Box>
                                                                )}
                                                                variant='outlined'
                                                                size='small'
                                                            />
                                                        )}
                                                        <Typography variant='body2' marginLeft={1} color='text.secondary'>
                                                            {Formatter.date(new Date(x.lastModified))}
                                                        </Typography>
                                                        <Box marginTop={1} gap={2} display='flex' flexDirection='row'>
                                                            {!isThisFlow && (
                                                                <Button
                                                                    size='small'
                                                                    variant='contained'
                                                                    disabled={isCloning}
                                                                    onClick={() => openFlowVersion(x.uuid)}>
                                                                    View
                                                                </Button>
                                                            )}
                                                            <LoadingButton
                                                                size='small'
                                                                variant='text'
                                                                loading={isThisPending && isCloning}
                                                                disabled={isCloning && !isThisPending}
                                                                onClick={() => startClone(x.uuid)}>
                                                                New Version
                                                            </LoadingButton>
                                                        </Box>
                                                    </TimelineContent>
                                                </TimelineItem>
                                            )
                                        })}
                                </Timeline>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>
            </Modal>
        </>
    )
}

export default memo(FlowSettings)
